import { Injectable, inject } from '@angular/core';
import { Api3Service } from '@app/services/api3.service';
import { GoogleTagService } from '@app/services/google-tag-manager.service';
import { AuthService } from './auth.service';
import { environment } from '@env';
import * as Sentry from '@sentry/angular';

/**
 * Event Tracking Schema v2.0
 * Format: CCSSNN
 * CC = Category (2 digits)
 * SS = Subcategory (2 digits)
 * NN = Sequence (2 digits)
 */

export const EVENT_SCHEMA = {
  // 01xxxx - Infrastructure
  INFRASTRUCTURE: {
    PAGE: {
      ROUTING: '010001',
    },
  },

  // 02xxxx - Authentication
  AUTH: {
    DEVICE_LOGIN: {
      CODE_CREATED: '021001',
      CODE_REDEEMED: '021002',
      CODE_INVALIDATED_BY_ADMIN: '021003',
    },
    LOGOUT: {
      DEVICE: '022001',
      USER: '022002',
    },
  },

  // 03xxxx - Core Product
  CORE_PRODUCT: {
    QA: {
      ANSWER_GENERATED: '031001',
      QUESTION_STARTED: '031002',
      QUESTION_SUBMITTED: '031003',
      USER_FEEDBACK_SENT: '031004',
    },
    VEHICLE_ID: {
      START: '032001',
      TRIGGERED: '032002',
      IDENTIFIED: '032003',
      UNAVAILABLE_KBA: '032004',
      UNAVAILABLE_VIN: '032005',
    },
    UI: {
      THREAD_TAB_CLICKED: '033001',
      THREAD_CLOSED: '033002',
    },
  },

  // 04xxxx - Business Operations
  BUSINESS: {
    SUBSCRIPTION: {
      CANCELLED: '041001',
      REACTIVATED: '041002',
    },
    COMPANY: {
      DELETED: '042001',
    },
  },

  // 05xxxx - Data Providers
  DATA_PROVIDERS: {
    HAYNESPRO: {
      REQUESTED: '051001',
    },
    MERCEDES: {
      REQUESTED: '052001',
      FROM_CHAT: '052002',
      FROM_DOC: '052003',
    },
    HGS: {
      REQUESTED: '053001',
    },
    VW: {
      REQUESTED: '054001',
    },
  },
} as const;

@Injectable({
  providedIn: 'root',
})
export class EventTrackerInternalService {
  authService = inject(AuthService);
  googleTagService = inject(GoogleTagService);
  api3Service = inject(Api3Service);

  eventTrackingEnabled = environment.enableEventTracking;

  public async trackEvent(eventCode: string, payload?: Record<string, unknown>) {
    if (!this.eventTrackingEnabled) return;

    const userIsLoggedIn = await this.authService.isLoggedIn();
    if (!userIsLoggedIn) {
      console.warn('[EventTrackerInternal] User is not logged in. Event not tracked:', eventCode, payload);
      return;
    }

    const { error } = await this.api3Service.api.eventTrackerInternal.trackEvent({
      eventCode: eventCode as unknown as object,
      payload: payload || {},
    });

    if (error) {
      console.error('[EventTrackerInternal] API-Error while calling trackEvent().', eventCode, payload);
      Sentry.captureException(error, {
        tags: {
          location: 'EventTrackerInternalService',
          feature: 'EventTrackerInternal',
          message: 'API-Error while calling trackEvent().',
        },
        extra: {
          eventCode,
          payload,
        },
      });
    }

    // @TODO : Add Google Tag Manager integration here if we want to track these events in GTM as well - i.e pure FE ones for marketing/analytics etc.
  }
}
